/**
 * Form Styles Hook
 *
 * Centralized form styling constants used across all modal and form components.
 * Provides consistent styling for inputs, containers, labels, and other form elements.
 *
 * Usage:
 * ```tsx
 * const formStyles = useFormStyles();
 * <Input style={formStyles.inputStyle} />
 * ```
 */

import { theme } from 'antd';
import { controlHeightTokens, radiusTokens } from './tokens';

export const useFormStyles = () => {
  const { token } = theme.useToken();

  return {
    /**
     * Standard input field styling
     * Height: 44px, rounded corners, full width
     */
    inputStyle: {
      borderRadius: radiusTokens.card, // 8px (card rung — equivalent px to the old token.borderRadius)
      height: `${controlHeightTokens.controlHeightXL}px`,
      width: '100%',
    },

    /**
     * Gray background container for form field groups
     * Used to visually group related fields
     */
    fieldContainerStyle: {
      marginBottom: '12px',
      padding: '12px',
      backgroundColor: token.colorFillQuaternary,
      borderRadius: radiusTokens.panel, // 12px (panel rung — equivalent px to the old token.borderRadiusLG)
    },

    /**
     * Standard label styling
     * Small font, tight spacing (4px), secondary color for visual hierarchy
     * Matches vendor EditBasicInfoModal reference style
     */
    labelStyle: {
      display: 'block',
      marginBottom: '4px',
      fontSize: token.fontSizeSM,
      color: token.colorTextSecondary,
    },

    /**
     * Section title styling (bold labels for sections)
     * Standard spacing: 16px bottom margin for proper visual hierarchy
     */
    sectionTitleStyle: {
      display: 'block',
      fontSize: token.fontSize,
      color: token.colorText,
      marginBottom: '16px',
      fontWeight: 600,
    },

    /**
     * Error message styling
     */
    errorStyle: {
      fontSize: token.fontSizeSM,
      color: token.colorError,
      marginTop: '4px',
    },

    /**
     * Modal footer button styling
     */
    modalButtonStyle: {
      height: `${controlHeightTokens.controlHeightXL}px`,
      fontSize: token.fontSizeSM,
      borderRadius: radiusTokens.card, // 8px (card rung — equivalent px to the old token.borderRadius)
    },
  };
};
